import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Logo from "../components/Logo";

const inputCls = "w-full mt-2 px-3 py-3 border border-zinc-300 focus:outline-none focus:border-[#0033FF] focus:ring-2 focus:ring-[#0033FF]/20 text-base";

export default function AcceptInvite() {
  const { token } = useParams();
  const { acceptInvite } = useAuth();
  const nav = useNavigate();
  const [invite, setInvite] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/invites/${token}`)
      .then(({ data }) => setInvite(data))
      .catch((e) => setLoadErr(formatApiErrorDetail(e.response?.data?.detail) || "Invite not found"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return setErr("Password must be at least 8 characters");
    setErr(""); setBusy(true);
    try {
      await acceptInvite(token, name, password);
      nav("/dashboard");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-8">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-10"><Logo size={32} /><div className="text-lg font-black tracking-tighter">VIZIONCAST</div></div>
        {loadErr ? (
          <div data-testid="invite-error">
            <h1 className="text-3xl font-black tracking-tighter mb-3">Invite unavailable</h1>
            <p className="text-sm text-zinc-600 mb-6">{loadErr}</p>
            <Link to="/login" className="font-bold underline underline-offset-4 text-sm" data-testid="invite-goto-login">Go to sign in</Link>
          </div>
        ) : !invite ? (
          <Loader2 className="w-6 h-6 animate-spin text-[#0033FF]" data-testid="invite-loading" />
        ) : (
          <>
            <div className="text-xs tracking-[0.25em] uppercase text-[#0033FF] font-bold mb-3">You're invited</div>
            <h1 className="text-4xl font-black tracking-tighter mb-2" data-testid="invite-org-name">Join {invite.org_name}.</h1>
            <p className="text-sm text-zinc-600 mb-8">Set up your account for <span className="font-bold" data-testid="invite-email-display">{invite.email}</span>.</p>
            <form onSubmit={submit} className="space-y-5">
              <div>
                <label className="text-[10px] tracking-[0.25em] uppercase font-bold text-zinc-600">Your name</label>
                <input data-testid="accept-name-input" value={name} onChange={(e) => setName(e.target.value)} required className={inputCls} />
              </div>
              <div>
                <label className="text-[10px] tracking-[0.25em] uppercase font-bold text-zinc-600">Choose a password</label>
                <input data-testid="accept-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className={inputCls} />
              </div>
              {err && <div data-testid="accept-error" className="text-sm text-[#FF3B30] border-l-2 border-[#FF3B30] pl-3 py-1">{err}</div>}
              <button data-testid="accept-submit-btn" disabled={busy}
                className="w-full bg-[#0033FF] text-white px-4 py-3.5 text-sm font-bold tracking-wider uppercase hover:bg-zinc-900 disabled:opacity-50 flex items-center justify-center gap-2">
                {busy ? "Joining…" : <>Join team <ArrowRight className="w-4 h-4" /></>}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
